import { useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogProps,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { Control, Controller, Path, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AdminProductDTO, IMAGE_MIN_PER_PRODUCT, PRICE_LEVELS } from '@src/common-utils/DataModels';
import { ServerError, isAuthError } from '@src/common-utils/AuthModels';
import { Nullable } from '@src/common-utils/Models';
import { useUserContext } from '@src/contexts/UserContextProvider';
import { ROUTES } from '@src/layout/RoutePage';
import {
  ProductFormValues,
  ProductImageItem,
  emptyProductFormValues,
  finalImageKeys,
  newImageItems,
  productFormSchema,
  productToFormValues,
  toCreateProductDTO,
  toUpdateProductDTO,
} from '@src/components/product-management/productForm';
import { ProductImagesField } from '@src/components/product-management/ProductImagesField';
import {
  adminProductsQueryKey,
  uploadNewProductImages,
  useCreateProduct,
  useUpdateProduct,
} from '@src/queries/ProductManagementQuery';

export type ProductDialogProps = {
  open: boolean;
  mode: 'CREATE' | 'UPDATE';
  /** the product being edited (UPDATE) */
  product?: Nullable<AdminProductDTO>;
  onClose: () => void;
  onSuccess: (product: AdminProductDTO) => void;
};

export const ProductDialog = ({ open, ...rest }: ProductDialogProps) => {
  const handleClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick') {
      rest.onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={handleClose}
      aria-labelledby="product-dialog-title"
    >
      <ProductDetail {...rest} />
    </Dialog>
  );
};

const FormTextField = ({
  control,
  name,
  label,
  helperText,
  ...rest
}: { control: Control<ProductFormValues>; name: Path<ProductFormValues>; label: string } & Omit<
  TextFieldProps,
  'name' | 'label' | 'value' | 'onChange' | 'error'
>) => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState }) => (
      <TextField
        {...field}
        {...rest}
        label={label}
        fullWidth
        error={!!fieldState.error}
        helperText={fieldState.error?.message ?? helperText}
      />
    )}
  />
);

const toImageItems = (product?: Nullable<AdminProductDTO>): ProductImageItem[] =>
  (product?.images ?? []).map((image) => ({
    kind: 'existing',
    id: `existing-${image.name}`,
    key: image.name,
    url: image.url,
  }));

const ProductDetail = ({ mode, product, onClose, onSuccess }: Omit<ProductDialogProps, 'open'>) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useUserContext();

  const { createProduct } = useCreateProduct();
  const { updateProduct } = useUpdateProduct();

  const [imageItems, setImageItems] = useState<ProductImageItem[]>(() => toImageItems(product));
  const [imagesError, setImagesError] = useState<Nullable<string>>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<Nullable<string>>(null);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError: setFieldError,
    formState: { isSubmitting },
  } = useForm<ProductFormValues>({
    defaultValues: mode === 'UPDATE' && product ? productToFormValues(product) : emptyProductFormValues(),
    resolver: yupResolver(productFormSchema(mode)) as unknown as Resolver<ProductFormValues>,
  });

  const discountEnabled = watch('discount.enabled');
  const discountType = watch('discount.discountType');

  const handleSaveError = (e: unknown) => {
    if (isAuthError(e)) {
      logout().finally(() => navigate(ROUTES.signin));
      return;
    }

    if (e instanceof ServerError && e.statusCode === 409) {
      if (mode === 'CREATE') {
        setFieldError('code', { type: 'server', message: 'A product with this code already exists' });
      } else {
        // someone else saved first: refresh the list; this form still holds the old version
        queryClient.invalidateQueries(adminProductsQueryKey);
        setError('This product was changed by someone else. Close this window, open the product again and retry.');
      }
      return;
    }

    setError(e instanceof Error ? e.message : 'Failed to save the product');
  };

  const onSubmit = async (values: ProductFormValues) => {
    setError(null);
    setImagesError(null);
    setUploadProgress({});

    if (imageItems.length < IMAGE_MIN_PER_PRODUCT) {
      setImagesError('At least one image is required');
      return;
    }

    try {
      const code = mode === 'CREATE' ? values.code.trim() : (product as AdminProductDTO).code;

      // 1. upload any new images straight to S3 (nothing is saved if one fails)
      const uploadedKeys = await uploadNewProductImages(code, newImageItems(imageItems), (id, percent) =>
        setUploadProgress((previous) => ({ ...previous, [id]: percent })),
      );
      const imageKeys = finalImageKeys(imageItems, uploadedKeys);

      // 2. save the product with the final, ordered image keys
      const saved =
        mode === 'CREATE'
          ? await createProduct(toCreateProductDTO(values, imageKeys))
          : await updateProduct({
              code,
              dto: toUpdateProductDTO(values, imageKeys, (product as AdminProductDTO).version),
            });

      onSuccess(saved);
    } catch (e) {
      handleSaveError(e);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon />
          <Typography variant="h5" id="product-dialog-title">
            {mode === 'CREATE' ? 'Add' : 'Edit'} Product
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={() => onClose()} disabled={isSubmitting}>
          <CloseIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormTextField
              control={control}
              name="code"
              label="Code"
              required
              disabled={mode === 'UPDATE' || isSubmitting}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormTextField
              control={control}
              name="name"
              label="Product name / group"
              required
              disabled={isSubmitting}
              helperText="Products with the same name are grouped together in search"
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              name="description"
              label="Description"
              multiline
              minRows={2}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              control={control}
              name="specificationText"
              label="Specification"
              multiline
              minRows={2}
              disabled={isSubmitting}
              helperText="One entry per line"
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormTextField control={control} name="packing" label="Packing" required disabled={isSubmitting} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormTextField control={control} name="unit" label="Unit" required disabled={isSubmitting} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormTextField
              control={control}
              name="listPrice"
              label="List price"
              required
              disabled={isSubmitting}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1">Price levels</Typography>
            <Typography variant="caption" color="text.secondary">
              Leave a level blank to charge the list price to customers on that level.
            </Typography>
          </Grid>
          {PRICE_LEVELS.map((level) => (
            <Grid item xs={6} sm={2.4} key={level}>
              <FormTextField
                control={control}
                name={`priceLevels.${level}`}
                label={`Level ${level}`}
                disabled={isSubmitting}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Controller
              control={control}
              name="discount.enabled"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={field.onChange} disabled={isSubmitting} />}
                  label="Discount"
                />
              )}
            />
          </Grid>
          {discountEnabled && (
            <>
              <Grid item xs={12} sm={4}>
                <Controller
                  control={control}
                  name="discount.discountType"
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label="Type"
                      disabled={isSubmitting}
                      {...field}
                      onChange={(event) => {
                        field.onChange(event);
                        if (event.target.value === 'PERCENTAGE') {
                          setValue('discount.discountYValue', '');
                        }
                      }}
                    >
                      <MenuItem value="PERCENTAGE">Percentage off</MenuItem>
                      <MenuItem value="BUY_X_GET_Y_FREE">Buy X get Y free</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormTextField
                  control={control}
                  name="discount.description"
                  label="Description"
                  disabled={isSubmitting}
                  helperText="Shown to customers, for example 'Buy 1 Get 1 Free'"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormTextField
                  control={control}
                  name="discount.discountValue"
                  label={discountType === 'PERCENTAGE' ? 'Percentage (%)' : 'Buy quantity (X)'}
                  disabled={isSubmitting}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              {discountType === 'BUY_X_GET_Y_FREE' && (
                <Grid item xs={6} sm={4}>
                  <FormTextField
                    control={control}
                    name="discount.discountYValue"
                    label="Free quantity (Y)"
                    disabled={isSubmitting}
                    inputProps={{ inputMode: 'numeric' }}
                    helperText="Blank = 1"
                  />
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <ProductImagesField
              items={imageItems}
              onChange={(items) => {
                setImagesError(null);
                setImageItems(items);
              }}
              disabled={isSubmitting}
              progress={uploadProgress}
              error={imagesError}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <LoadingButton
              variant="text"
              onClick={() => onClose()}
              disabled={isSubmitting}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </LoadingButton>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting} sx={{ textTransform: 'none' }}>
              {mode === 'CREATE' ? 'Add product' : 'Save changes'}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
